import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Moon, Sun, LogOut, ShieldCheck, Truck } from "lucide-react";

export default function TopNavbar({ onMenu }) {
  const { user, logout } = useAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("mf_theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("mf_theme", dark ? "dark" : "light");
  }, [dark]);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-card/80 px-4 backdrop-blur-md sm:px-6">
      <button className="lg:hidden text-muted-foreground" onClick={onMenu} data-testid="mobile-menu-button">
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex items-center gap-2">
        <span className="hidden text-sm font-medium text-foreground sm:inline">Store Operations</span>
        <span className="hidden rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-mono text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 md:inline">
          ● Open
        </span>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        <span className="hidden text-xs font-mono uppercase tracking-wider text-muted-foreground sm:inline">{today}</span>
        <Button variant="ghost" size="icon" onClick={() => setDark((d) => !d)} data-testid="theme-toggle" aria-label="toggle theme">
          {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-secondary" data-testid="user-menu-trigger">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {initials}
              </span>
              <span className="hidden text-left leading-tight sm:block">
                <span className="block text-sm font-medium">{user?.name}</span>
                <span className="block text-[10px] font-mono uppercase text-muted-foreground">{user?.role}</span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center gap-2">
              {user?.role === "admin" ? <ShieldCheck className="h-4 w-4 text-primary" /> : <Truck className="h-4 w-4 text-primary" />}
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
